import { useCallback, useEffect, useState } from "react";
import { Check, Inbox, Loader2, RefreshCw } from "lucide-react";
import api from "../api/axios";

export default function AdminContactMessagesPage() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [resolvingId, setResolvingId] = useState("");

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/contact-messages");
      setMessages(Array.isArray(data) ? data : []);
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Failed to load contact messages.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const resolveMessage = async (id) => {
    setResolvingId(id);
    setError("");
    try {
      const { data } = await api.patch(`/admin/contact-messages/${id}/resolve`);
      setMessages((current) => current.map((message) => message._id === id ? data : message));
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Failed to update this message.");
    } finally {
      setResolvingId("");
    }
  };

  const newCount = messages.filter((message) => message.status === "new").length;
  const resolvedCount = messages.length - newCount;
  const visibleMessages = messages.filter((message) => filter === "all" || message.status === filter);

  return (
    <div className="mx-auto max-w-7xl space-y-6 text-white">
      <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-sm uppercase tracking-[0.24em] text-cyan-300/80">Admin</p>
            <h1 className="mt-2 text-3xl font-bold text-white">Contact Inbox</h1>
            <p className="mt-2 text-sm text-slate-400">Messages submitted through the public contact form.</p>
          </div>
          <button
            type="button"
            onClick={fetchMessages}
            disabled={loading}
            aria-busy={loading}
            aria-label="Refresh contact messages"
            title="Refresh contact messages"
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-cyan-300/30 bg-cyan-300/10 text-cyan-300 transition hover:bg-cyan-300/20 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw size={17} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-white/10 bg-slate-900/75 p-5">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">All messages</p>
          <p className="mt-3 text-3xl font-bold text-white">{messages.length}</p>
        </div>
        <div className="rounded-2xl border border-amber-400/20 bg-amber-400/5 p-5">
          <p className="text-xs uppercase tracking-[0.2em] text-amber-200/80">New</p>
          <p className="mt-3 text-3xl font-bold text-amber-300">{newCount}</p>
        </div>
        <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/5 p-5">
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-200/80">Resolved</p>
          <p className="mt-3 text-3xl font-bold text-emerald-300">{resolvedCount}</p>
        </div>
      </section>

      <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-4 backdrop-blur-xl md:p-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Inbox className="text-cyan-300" size={20} />
            <h2 className="text-base font-semibold text-white">Submissions</h2>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-400">
            Status
            <select
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              className="rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white outline-none focus:border-cyan-300/50"
            >
              <option value="all">All</option>
              <option value="new">New</option>
              <option value="resolved">Resolved</option>
            </select>
          </label>
        </div>

        {error && <p role="alert" className="mb-4 rounded-xl border border-red-400/20 bg-red-400/10 p-3 text-sm text-red-200">{error}</p>}
        {loading && (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-slate-400">
            <Loader2 className="animate-spin text-cyan-300" size={20} /> Loading messages...
          </div>
        )}
        {!loading && !error && visibleMessages.length === 0 && (
          <div className="py-12 text-center text-sm text-slate-400">
            {messages.length === 0 ? "No contact messages yet." : "No messages match this status."}
          </div>
        )}
        {!loading && visibleMessages.length > 0 && (
          <div className="space-y-3">
            {visibleMessages.map((message) => (
              <article key={message._id} className="rounded-xl border border-white/10 bg-white/[0.04] p-4 md:p-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <h3 className="font-semibold text-white">{message.name}</h3>
                      <a className="break-all text-sm text-cyan-300 hover:underline" href={`mailto:${message.email}`}>
                        {message.email}
                      </a>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">{new Date(message.createdAt).toLocaleString()}</p>
                    <p className="mt-4 whitespace-pre-wrap break-words text-sm leading-6 text-slate-200">{message.message}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 sm:flex-col sm:items-end">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                      message.status === "resolved"
                        ? "bg-emerald-500/15 text-emerald-300"
                        : "bg-amber-500/15 text-amber-300"
                    }`}>
                      {message.status === "resolved" ? "Resolved" : "New"}
                    </span>
                    {message.status !== "resolved" && (
                      <button
                        type="button"
                        onClick={() => resolveMessage(message._id)}
                        disabled={Boolean(resolvingId)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-400/25 px-3 py-1.5 text-xs font-semibold text-emerald-300 transition hover:bg-emerald-400/10 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <Check size={14} /> {resolvingId === message._id ? "Saving..." : "Mark resolved"}
                      </button>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}